import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "layouts/Main";
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CAvatar from "components/Avatar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getInitials } from "helpers/users";
import { grey } from "@mui/material/colors";

import styles from "./UserProfile.module.scss";
import Select from "components/Select";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectUser } from "store/selectors";
import { fetchUser } from "store/appSlice";
import { editUser, editUserPassword } from "../../../../api/resources/users";
import { FormErrorProps } from "../../../../api/models/FormErrorProps";
import { UnprotectedPaths } from "../../../../routes";

const UserProfile = () => {
  const [snackbar, setSnackbar] = useState(null);

  const [profileErrors, setProfileErrors] = useState<FormErrorProps[] | null>(
    null,
  );
  const [passwordErrors, setPasswordErrors] = useState<FormErrorProps[] | null>(
    null,
  );

  const navigate = useNavigate();
  const { userId } = useParams();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);

  useEffect(() => {
    dispatch(fetchUser(Number(userId)));
  }, [dispatch, userId]);

  const snackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar(null);
  };

  const handleSave = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setProfileErrors(null);
    setPasswordErrors(null);

    const data = new FormData(event.currentTarget);
    const postData = {
      firstName: data.get("firstName"),
      lastName: data.get("lastName"),
      email: data.get("email"),
    };

    const emailChanged = postData.email !== user.email;

    editUser(Number(userId), postData)
      .then(() => {
        if (emailChanged) {
          localStorage.removeItem("token");
          setSnackbar({
            type: "success",
            message: "Email changed, Logging out.",
          });
          setTimeout(() => {
            navigate(`/${UnprotectedPaths.Login}`);
          }, 3000);
        } else {
          dispatch(fetchUser(Number(userId)));
        }
      })
      .catch((error) => {
        setProfileErrors(error?.response?.data?.errors || []);
        setSnackbar({
          type: "error",
          message: error.response.data.message || "An error occurred",
        });
      });
  };

  const handlePasswordChange = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    setProfileErrors(null);
    setPasswordErrors(null);

    const data = new FormData(event.currentTarget);
    const val = (n: string) => String(data.get(n) ?? "").trim();

    const oldPassword = val("oldPassword");
    const newPassword = val("newPassword");
    const repeatPassword = val("newPasswordRepeat");

    if (!oldPassword || !newPassword || !repeatPassword) {
      setSnackbar({
        type: "error",
        message: "All password fields are required",
      });
      return;
    }

    if (newPassword !== repeatPassword) {
      setSnackbar({ type: "error", message: "Passwords do not match" });
      return;
    }

    try {
      await editUserPassword(Number(userId), { oldPassword, newPassword });

      (event.target as HTMLFormElement).reset();
      setSnackbar({
        type: "success",
        message: "Password changed successfully.",
      });
    } catch (error: any) {
      setPasswordErrors(error?.response?.data?.errors || []);
      setSnackbar({
        type: "error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "An error occurred",
      });
    }
  };

  return (
    <MainLayout
      snackbar={{
        open: !!snackbar,
        handleClose: snackbarClose,
        type: snackbar?.type,
        message: snackbar?.message,
      }}
    >
      {user && (
        <>
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <IconButton
                sx={{ mr: "0.6rem" }}
                size="small"
                onClick={() => navigate(-1)}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography component="p">Back</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <CAvatar size="extralarge">
                {getInitials(user.firstName + " " + user.lastName)}
              </CAvatar>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  ml: 2,
                }}
              >
                <Typography variant="h5" component="h5">
                  {`${user.firstName} ${user.lastName}`}
                </Typography>
                <Typography component="p" sx={{ color: grey[600] }}>
                  {user.email}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Grid
            container
            columnSpacing={6}
            rowSpacing={4}
            sx={{ mt: "1rem", mb: "2rem" }}
          >
            <Grid item xs={12} md={12} lg={6}>
              <Box component="form" onSubmit={handleSave} noValidate>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Divider textAlign="left">Account information</Divider>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="firstName"
                      name="firstName"
                      label="First name"
                      size="small"
                      defaultValue={user.firstName}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="lastName"
                      name="lastName"
                      label="Last name"
                      size="small"
                      defaultValue={user.lastName}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id="email"
                      name="email"
                      label="Email"
                      size="small"
                      defaultValue={user.email}
                      required
                      fullWidth
                    />
                  </Grid>

                  {/* Companies */}
                  <Grid item xs={12}>
                    <Divider textAlign="left">Companies</Divider>
                  </Grid>
                  <Grid item xs={12}>
                    {user.linkedClients.length === 0 ? (
                      <Typography
                        component="p"
                        sx={{ color: grey[600], fontSize: 15 }}
                      >
                        User is not linked to any companies
                      </Typography>
                    ) : (
                      user.linkedClients.map((linkedClient) => (
                        <Card
                          key={linkedClient.id ?? linkedClient.clientName}
                          sx={{
                            p: 2,
                            mb: 2,
                            boxShadow: 0,
                            border: 1,
                            borderColor: grey[300],
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                            className={styles.companyCard}
                          >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <CAvatar size="medium">
                                {getInitials(linkedClient.clientName)}
                              </CAvatar>
                              <Box sx={{ ml: 2 }}>
                                <Typography>
                                  {linkedClient.clientName}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              display="flex"
                              justifyContent="flex-end"
                              alignItems="center"
                            >
                              {linkedClient.roleId === 1 ? (
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    textTransform: "uppercase",
                                    fontWeight: 500,
                                    letterSpacing: "0.4px",
                                    color: grey[600],
                                  }}
                                >
                                  Superadmin
                                </Typography>
                              ) : (
                                <Select
                                  label="Role"
                                  id="role"
                                  name="role"
                                  minWidth={150}
                                  defaultValue={linkedClient.roleId}
                                  disabled
                                  options={[
                                    { label: "User", value: 3 },
                                    { label: "Admin", value: 2 },
                                  ]}
                                />
                              )}
                            </Box>
                          </Box>
                        </Card>
                      ))
                    )}
                  </Grid>

                  {profileErrors && (
                    <Grid item xs={12}>
                      {profileErrors.map(
                        ({ message }: FormErrorProps, i: number) => (
                          <Alert
                            key={i}
                            variant="filled"
                            severity="error"
                            sx={{ mb: 0.4 }}
                          >
                            {message}
                          </Alert>
                        ),
                      )}
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Button variant="contained" type="submit">
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* RIGHT: Password form (lg=6) */}
            <Grid item xs={12} md={12} lg={6}>
              <Box component="form" onSubmit={handlePasswordChange} noValidate>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Divider textAlign="left">Change password</Divider>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="oldPassword"
                      label="Previous password"
                      type="password"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="newPassword"
                      label="New password"
                      type="password"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="newPasswordRepeat"
                      label="Repeat new password"
                      type="password"
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  {passwordErrors && (
                    <Grid item xs={12}>
                      {passwordErrors.map(
                        ({ message }: FormErrorProps, i: number) => (
                          <Alert
                            key={i}
                            variant="filled"
                            severity="error"
                            sx={{ mb: 0.4 }}
                          >
                            {message}
                          </Alert>
                        ),
                      )}
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Button variant="contained" type="submit">
                      Change password
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </MainLayout>
  );
};
export default UserProfile;
